/**
 * ATR TP / SL suggestion lines on a `.VEST` chart (#637), and the legend text
 * for `VestSizingLegend`. Display only: faint dotted price lines with no axis
 * tag, nothing to drag or click, `.VEST` stays non-executable.
 *
 * Off a `.VEST` chart, or with the "ATR sizing (Vest)" toggle off (or hidden
 * by Clean): nothing is watched (no requests), no rows or price are read, no
 * line is created and the legend is null.
 */

import { useEffect, useMemo, useRef } from 'react';
import type { IPriceLine } from 'lightweight-charts';
import { isVestFeedSymbol, vestFeedMarket, vestTickSize } from '../../utils/vestFeed';
import {
  clearVestPriceLines,
  syncVestPriceLines,
  vestChartSymbolFor,
  type PriceLineHost,
  type VestPriceLineSpec,
} from '../../utils/vestPositionLines';
import { deriveVestSizingLines, formatVestSizingLegend, type VestSizingLegend } from '../../utils/vestSizingLines';
import { useVestPositionRows } from '../../store/vestPositionsStore';
import { useVestSizingStore, watchVestSizing } from '../../store/vestSizingStore';

const NO_LINES: readonly VestPriceLineSpec[] = [];

export function useVestSizingLines(
  /** The price series' ref (only its price-line methods are used). */
  seriesRef: { readonly current: unknown },
  seriesVersion: number,
  symbol: string,
  lastPrice: number | null | undefined,
  /** The "ATR sizing (Vest)" level toggle, Clean already applied. */
  enabled: boolean,
): VestSizingLegend | null {
  const market = enabled && isVestFeedSymbol(symbol) ? vestFeedMarket(symbol) : null;
  const active = market !== null && vestChartSymbolFor(market) !== null;
  const watched = active ? market : null;

  useEffect(() => {
    if (!watched) return;
    return watchVestSizing(watched);
  }, [watched]);

  const entry = useVestSizingStore(s => (watched ? s.byMarket[watched] ?? null : null));
  const rows = useVestPositionRows(active);
  const tick = active ? vestTickSize(symbol) : null;
  const price = active ? lastPrice ?? null : null;

  const specs = useMemo(
    () => (active ? deriveVestSizingLines(entry, symbol, price, rows, tick) : NO_LINES),
    [active, entry, symbol, price, rows, tick],
  );
  const legend = useMemo(
    () => (active ? formatVestSizingLegend(entry, symbol, tick) : null),
    [active, entry, symbol, tick],
  );

  const held = useRef(new Map<string, { line: IPriceLine; spec: VestPriceLineSpec }>());

  // A new series (symbol / timeframe rebuild) or unmount: drop every line.
  useEffect(() => {
    const series = (seriesRef.current ?? null) as PriceLineHost<IPriceLine> | null;
    const map = held.current;
    return () => clearVestPriceLines(series, map);
  }, [seriesRef, seriesVersion, symbol]);

  useEffect(() => {
    const series = (seriesRef.current ?? null) as PriceLineHost<IPriceLine> | null;
    if (!series) return;
    syncVestPriceLines(series, held.current, specs);
  }, [seriesRef, seriesVersion, symbol, specs]);

  return legend;
}
